import React, { FunctionComponent, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { ApiRetailerPaymentType } from '@vend/types'
import { useT } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import { selectOutstandingBalance } from '../../../state/slices/currentSale/currentSaleSlice'
import { useHotkeysService } from '../../../ng-compat/hooks'
import { usePaymentDisabilityCheck } from './hooks/usePaymentDisabilityCheck'

export interface Props {
  amountTendered: number | string
  hasValidTenderedAmount: boolean
  filteredPaymentTypes: ApiRetailerPaymentType[]
  systemPaymentTypes: {
    roundingPaymentType?: ApiRetailerPaymentType
    currencyAdjustmentPaymentType?: ApiRetailerPaymentType
    storeCreditPaymentType?: ApiRetailerPaymentType
    loyaltyPaymentType?: ApiRetailerPaymentType
    lspayPaymentType?: ApiRetailerPaymentType
  }
  pay: (payment: ApiRetailerPaymentType) => void
}

export const PaymentOptions: FunctionComponent<Props> = ({
  amountTendered,
  hasValidTenderedAmount,
  filteredPaymentTypes,
  systemPaymentTypes,
  pay,
}: Props) => {
  const t = useT()
  const hotkeysService = useHotkeysService()

  const sortedPaymentTypes = [...filteredPaymentTypes].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const { checkIfPaymentDisabled } = usePaymentDisabilityCheck(
    amountTendered,
    hasValidTenderedAmount
  )
  const isLSPayAvailable = !!systemPaymentTypes.lspayPaymentType

  const getHotKey = useCallback(
    (index: number): number => {
      // We start from 1 (not 0).
      if (isLSPayAvailable) {
        // LSPay will be number one if present.
        return index + 2
      }

      return index + 1
    },
    [isLSPayAvailable]
  )

  useEffect(() => {
    const cleanUpFunctions: Array<() => void> = []

    sortedPaymentTypes.forEach((paymentType, index) => {
      const hotKey = getHotKey(index)
      const config = {
        summary: t('Select payment option {hotkey}', {
          _context:
            'Hot key summary for the payment option buttons on the payment screen',
          hotkey: hotKey,
        }),
        combo: `alt+${hotKey}`,
        count: true,
        action: () => pay(paymentType),
      }
      hotkeysService.add(config)
      cleanUpFunctions.push(() => hotkeysService.remove(config.combo))
    })

    return () => {
      cleanUpFunctions.forEach(func => func())
    }
  }, [t, pay, getHotKey, hotkeysService, sortedPaymentTypes])

  return (
    <div
      id="payment-options-group"
      className="payment-type-button-group vd-btn-group vd-mt4"
    >
      {sortedPaymentTypes.map((paymentType, index) => {
        return (
          <button
            data-hotkey-tip={getHotKey(index)}
            key={paymentType.id}
            data-id={paymentType.id}
            className={classNames('vd-btn vd-btn--jumbo btn--payment-type', {
              'vd-btn--no': outstandingBalance < 0,
              'vd-btn--supplementary':
                outstandingBalance >= 0 && systemPaymentTypes.lspayPaymentType,
              'vd-btn--go':
                outstandingBalance >= 0 && !systemPaymentTypes.lspayPaymentType,
            })}
            disabled={checkIfPaymentDisabled(paymentType)}
            onClick={() => pay(paymentType)}
          >
            {paymentType.name}
          </button>
        )
      })}
    </div>
  )
}
